/**
 * ConnectWise PSA API client for the Outlook Email Logger.
 * Handles contact sync, activity creation, and email matching.
 */

interface CWConfig {
  baseUrl: string;
  auth: string; // Base64 encoded "companyId+publicKey:privateKey"
  clientId: string;
  memberId: number;
  logTimeOnOpps?: boolean;       // default true — pre-sales time tracking on opportunities
  defaultAddTimeEntry?: boolean; // default false — whether the Add Time Entry switch starts on
  credsStoredAt?: number;        // Unix ms, set when SetupPane saves. Drives the "rotate your CW creds" warning.
}

/**
 * Proactive API-key-age warning. CW Manage API member keys don't have
 * a hard expiry, but tenants frequently rotate them on 90-180 day
 * cadences — and we've seen cases where revoked keys cause silent
 * "my tool stopped working" tickets. Surface a banner once creds are
 * this many days old so the user rotates before they're caught off-guard.
 */
export const CREDS_AGE_WARN_DAYS = 180;
export const CREDS_AGE_EXPIRED_DAYS = 365;

export type CredsAgeBucket = 'fresh' | 'warn' | 'expired';

export function credsAgeBucket(): { bucket: CredsAgeBucket; days: number | null } {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return { bucket: 'fresh', days: null };
    const cfg = JSON.parse(stored) as CWConfig;
    if (!cfg.credsStoredAt) return { bucket: 'fresh', days: null };
    const ageMs = Date.now() - cfg.credsStoredAt;
    const days = Math.floor(ageMs / (24 * 60 * 60 * 1000));
    if (days >= CREDS_AGE_EXPIRED_DAYS) return { bucket: 'expired', days };
    if (days >= CREDS_AGE_WARN_DAYS) return { bucket: 'warn', days };
    return { bucket: 'fresh', days };
  } catch {
    return { bucket: 'fresh', days: null };
  }
}

export function markCredsJustStored() {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return;
    const cfg = JSON.parse(stored) as CWConfig;
    cfg.credsStoredAt = Date.now();
    localStorage.setItem('cw_config', JSON.stringify(cfg));
  } catch {
    // ignore
  }
}

export function getLogTimeOnOpps(): boolean {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return true;
    const config = JSON.parse(stored) as CWConfig;
    return config.logTimeOnOpps !== false; // undefined → true (default on)
  } catch {
    return true;
  }
}

export function setLogTimeOnOpps(value: boolean) {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return;
    const config = JSON.parse(stored) as CWConfig;
    config.logTimeOnOpps = value;
    localStorage.setItem('cw_config', JSON.stringify(config));
  } catch {
    // ignore
  }
}

/**
 * Whether the "Add time entry" switch should default ON when the
 * pane opens. Default is OFF (false) — user opts in per activity.
 * Toggled from the Settings view in OEL and CML.
 */
export function getDefaultAddTimeEntry(): boolean {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return false;
    const cfg = JSON.parse(stored) as CWConfig & { defaultAddTimeEntry?: boolean };
    return cfg.defaultAddTimeEntry === true;
  } catch {
    return false;
  }
}

export function setDefaultAddTimeEntry(value: boolean) {
  try {
    const stored = localStorage.getItem('cw_config');
    if (!stored) return;
    const config = JSON.parse(stored) as CWConfig & { defaultAddTimeEntry?: boolean };
    config.defaultAddTimeEntry = value;
    localStorage.setItem('cw_config', JSON.stringify(config));
  } catch {
    // ignore
  }
}

function getConfig(): CWConfig {
  const stored = localStorage.getItem('cw_config');
  if (!stored) throw new Error('CW not configured');
  return JSON.parse(stored);
}

export function isConfigured(): boolean {
  return !!localStorage.getItem('cw_config');
}

export function saveConfig(config: CWConfig) {
  localStorage.setItem('cw_config', JSON.stringify(config));
}

export function clearConfig() {
  localStorage.removeItem('cw_config');
  localStorage.removeItem('cw_contacts');
  localStorage.removeItem('cw_last_sync');
}

function getBaseUrl(): string {
  // Always use /cwapi proxy (both dev and production) to avoid CORS
  return '/cwapi';
}

/**
 * The ConnectWise regional API host the proxy should route to, derived from
 * the configured baseUrl (e.g. https://api-eu.myconnectwise.net/... → api-eu.
 * myconnectwise.net). Sent as the `x-cw-base` header so server.js can pick the
 * datacenter from its allowlist instead of hardcoding NA. Falls back to NA if
 * baseUrl is absent or unparseable.
 */
function cwBaseHost(config: CWConfig): string {
  try {
    return new URL(config.baseUrl).hostname;
  } catch {
    return 'api-na.myconnectwise.net';
  }
}

/**
 * Exponential-backoff retry around CW API calls. Parallels ICL-23.
 * Retries on:
 *   - 429 Too Many Requests (honors Retry-After header when present)
 *   - 500 / 502 / 503 / 504 (transient server errors)
 *   - Network failure (fetch throws, typically offline or DNS blip)
 * Does NOT retry on 4xx client errors (401/403/404/400 etc.) — those
 * are deterministic, retrying just amplifies the real problem.
 *
 * Schedule: 1s, 2s, 4s, each with up to ±30% jitter to avoid thundering
 * herds when many users all hit a rate limit at once.
 */
const RETRY_DELAYS_MS = [1000, 2000, 4000];

function jitter(ms: number): number {
  const factor = 0.7 + Math.random() * 0.6; // 0.7x..1.3x
  return Math.round(ms * factor);
}

function shouldRetry(status: number): boolean {
  return status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
}

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

async function cwFetch<T>(path: string, opts: { method?: string; body?: unknown; params?: Record<string, string> } = {}): Promise<T> {
  const config = getConfig();
  const { method = 'GET', body, params } = opts;
  const url = new URL(`${getBaseUrl()}${path}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      url.searchParams.set(k, v);
    }
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method,
        headers: {
          'Authorization': `Basic ${config.auth}`,
          'clientId': config.clientId,
          'Content-Type': 'application/json',
          'x-cw-base': cwBaseHost(config),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      // Network failure (offline, DNS, CORS preflight reject, etc.)
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(jitter(RETRY_DELAYS_MS[attempt]));
        continue;
      }
      throw lastError;
    }

    if (res.ok) return res.json();

    if (shouldRetry(res.status) && attempt < RETRY_DELAYS_MS.length) {
      // Honor Retry-After (seconds) if present; otherwise use the scheduled delay.
      const retryAfter = res.headers.get('Retry-After');
      const delayMs = retryAfter && !Number.isNaN(Number(retryAfter))
        ? Number(retryAfter) * 1000
        : jitter(RETRY_DELAYS_MS[attempt]);
      await sleep(delayMs);
      continue;
    }

    const errBody = await res.text().catch(() => 'Unknown error');
    throw new Error(`CW API ${method} ${path}: ${res.status} ${errBody}`);
  }

  // Unreachable in practice — loop either returns or throws.
  throw lastError ?? new Error(`CW API ${method} ${path}: retries exhausted`);
}

// ─── Types ──────────────────────────────────────────────────────

export interface CWContact {
  id: number;
  firstName: string;
  lastName: string;
  company: { id: number; name: string } | null;
  emails: string[];
  phones: string[];
}

interface CWContactRaw {
  id: number;
  firstName?: string;
  lastName?: string;
  company?: { id: number; name?: string };
  communicationItems?: { type?: { name?: string }; value?: string }[];
}

export interface CWMember {
  id: number;
  firstName?: string;
  lastName?: string;
  identifier?: string;
}

export interface CWActivityType {
  id: number;
  name?: string;
}

// ─── Contacts ───────────────────────────────────────────────────

/**
 * Extract a usable CWContact from CW's raw /company/contacts record.
 *
 * Email detection keys on the VALUE (`includes('@')`), NOT on the comm-item
 * `type.name`. CW's `fields=` selection can return communicationItems with the
 * nested `type` object stripped/empty; the old type-based filter then matched
 * nothing -> the contact's emails came back `[]` -> the contact was dropped
 * entirely (`if (emails.length === 0) continue`) -> empty picker. That code was
 * unchanged since the original commit, so a CW-side response-shape change alone
 * regressed it (BAS-18: "the email IS there + it used to work"). Value-based
 * detection is shape-independent and immune to the strip.
 *
 * Contacts with NO email are still returned (never dropped) so the picker can
 * list them and offer to add an email — they just won't auto-match by address.
 */
export function extractContact(raw: CWContactRaw): CWContact {
  const items = raw.communicationItems || [];

  const emails: string[] = [];
  const seenEmail = new Set<string>();
  for (const ci of items) {
    const v = (ci.value || '').toLowerCase().trim();
    if (!v.includes('@') || seenEmail.has(v)) continue;
    seenEmail.add(v);
    emails.push(v);
  }

  // A phone is any non-email value containing a digit — also type-independent,
  // so a stripped `type` doesn't lose it. Display-only; dedup order-preserving.
  const phones: string[] = [];
  const seenPhone = new Set<string>();
  for (const ci of items) {
    const v = (ci.value || '').trim();
    if (!v || v.includes('@') || !/\d/.test(v) || seenPhone.has(v)) continue;
    seenPhone.add(v);
    phones.push(v);
  }

  return {
    id: raw.id,
    firstName: raw.firstName || '',
    lastName: raw.lastName || '',
    company: raw.company ? { id: raw.company.id, name: raw.company.name || '' } : null,
    emails,
    phones,
  };
}

export async function syncContacts(onProgress?: (page: number, count: number) => void): Promise<CWContact[]> {
  // Initial-sync jitter: N users opening OEL/CML for the first time on
  // Monday morning all hit CW's /company/contacts in the same second,
  // bursting past CW's ~40 req/sec tenant limit. A random 0-8s delay
  // on the FIRST sync spreads the load enough to stay under the limit
  // for teams of ~20. Subsequent syncs (cache refresh) have no delay.
  const lastSync = getLastSyncDate();
  if (!lastSync) {
    const jitterMs = Math.floor(Math.random() * 8000);
    onProgress?.(0, 0);
    await new Promise(r => setTimeout(r, jitterMs));
  }

  const allContacts: CWContact[] = [];
  let page = 1;

  while (true) {
    onProgress?.(page, allContacts.length);
    const batch = await cwFetch<CWContactRaw[]>('/company/contacts', {
      params: {
        pageSize: '1000',
        page: String(page),
        fields: 'id,firstName,lastName,company,communicationItems',
      },
    });

    // Keep every contact — even those with no email — so the picker can list
    // them and offer to add one (Gary). Email-less contacts simply won't
    // auto-match by address. Extraction is value-based (see extractContact),
    // immune to CW's comm-item type-strip that regressed the old filter.
    for (const raw of batch) {
      allContacts.push(extractContact(raw));
    }

    if (batch.length < 1000) break;
    page++;
  }

  // Cache locally
  localStorage.setItem('cw_contacts', JSON.stringify(allContacts));
  localStorage.setItem('cw_last_sync', new Date().toISOString());

  return allContacts;
}

export function getCachedContacts(): CWContact[] {
  const stored = localStorage.getItem('cw_contacts');
  return stored ? JSON.parse(stored) : [];
}

export function getLastSyncDate(): string | null {
  return localStorage.getItem('cw_last_sync');
}

/** Get cached member emails (internal team — excluded from contact matches) */
export function getCachedMemberEmails(): Set<string> {
  const stored = localStorage.getItem('cw_member_emails');
  return stored ? new Set(JSON.parse(stored)) : new Set();
}

/** Cache member emails for exclusion from contact matches */
export async function syncMemberEmails(): Promise<void> {
  try {
    const members = await fetchMembers();
    // Members don't have emails in the basic response, but their identifier
    // is often their email. Also cache the config member ID's email.
    const emails = members
      .map(m => m.identifier?.toLowerCase())
      .filter((e): e is string => !!e && e.includes('@'));
    localStorage.setItem('cw_member_emails', JSON.stringify(emails));
  } catch {
    // Non-fatal — just won't exclude members
  }
}

/** Match email addresses against cached CW contacts (excludes internal members) */
export function matchEmails(emailAddresses: string[]): CWContact[] {
  const contacts = getCachedContacts();
  const memberEmails = getCachedMemberEmails();
  const normalized = emailAddresses.map(e => e.toLowerCase().trim());
  const matched: CWContact[] = [];
  const seen = new Set<number>();

  for (const email of normalized) {
    // Skip internal member emails
    if (memberEmails.has(email)) continue;

    for (const contact of contacts) {
      if (!seen.has(contact.id) && contact.emails.includes(email)) {
        matched.push(contact);
        seen.add(contact.id);
      }
    }
  }

  return matched;
}

// ─── Companies ──────────────────────────────────────────────────

export interface CWCompany {
  id: number;
  name: string;
  identifier?: string;
}

export async function searchCompanies(query: string): Promise<CWCompany[]> {
  if (query.length < 2) return [];
  return cwFetch('/company/companies', {
    params: {
      pageSize: '20',
      fields: 'id,name,identifier',
      conditions: `name contains "${query}"`,
      orderBy: 'name asc',
    },
  });
}

export async function fetchTicketsForCompany(companyId: number, includeClosed = false): Promise<CWTicket[]> {
  let conditions = `company/id=${companyId}`;
  if (!includeClosed) conditions += ' AND closedFlag=false';
  return cwFetch('/service/tickets', {
    params: {
      pageSize: '100',
      fields: 'id,summary,status,board,company,priority,closedFlag',
      conditions,
      orderBy: 'id desc',
    },
  });
}

export async function fetchOpportunitiesForCompany(companyId: number, includeClosed = false): Promise<CWOpportunity[]> {
  let conditions = `company/id=${companyId}`;
  if (!includeClosed) conditions += ' AND closedDate=null';
  return cwFetch('/sales/opportunities', {
    params: {
      pageSize: '100',
      fields: 'id,name,stage,status,company,contact,closedDate',
      conditions,
      orderBy: 'id desc',
    },
  });
}

// ─── Tickets ────────────────────────────────────────────────────

export interface CWTicket {
  id: number;
  summary?: string;
  status?: { id: number; name?: string };
  board?: { id: number; name?: string };
  company?: { id: number; name?: string };
  priority?: { id: number; name?: string };
  closedFlag?: boolean;
}

export async function fetchTicketsForContact(contactId: number, includeClosed = false): Promise<CWTicket[]> {
  let conditions = `contact/id=${contactId}`;
  if (!includeClosed) conditions += ' AND closedFlag=false';
  return cwFetch('/service/tickets', {
    params: {
      pageSize: '100',
      fields: 'id,summary,status,board,company,priority,closedFlag',
      conditions,
      orderBy: 'id desc',
    },
  });
}

export interface CWStatus {
  id: number;
  name?: string;
}

export async function fetchBoardStatuses(boardId: number): Promise<CWStatus[]> {
  return cwFetch(`/service/boards/${boardId}/statuses`, {
    params: { pageSize: '50', fields: 'id,name' },
  });
}

export async function updateTicketStatus(ticketId: number, statusId: number): Promise<void> {
  await cwFetch(`/service/tickets/${ticketId}`, {
    method: 'PATCH',
    body: [{ op: 'replace', path: 'status', value: { id: statusId } }],
  });
}

export async function createTicket(params: {
  summary: string;
  contactId: number;
  companyId: number;
  description?: string;
  boardId?: number;
}): Promise<{ id: number }> {
  const body: Record<string, unknown> = {
    summary: params.summary,
    company: { id: params.companyId },
    contact: { id: params.contactId },
  };
  if (params.description) body.initialDescription = params.description;
  if (params.boardId) body.board = { id: params.boardId };
  return cwFetch('/service/tickets', { method: 'POST', body });
}

export async function fetchServiceBoards(): Promise<{ id: number; name: string }[]> {
  return cwFetch('/service/boards', {
    params: { pageSize: '100', fields: 'id,name', conditions: 'inactiveFlag=false' },
  });
}

// ─── Opportunities ──────────────────────────────────────────────

export interface CWOpportunity {
  id: number;
  name?: string;
  stage?: { id: number; name?: string };
  status?: { id: number; name?: string };
  company?: { id: number; name?: string };
  contact?: { id: number; name?: string };
  closedDate?: string | null;
}

export async function fetchOpportunitiesForContact(contactId: number, includeClosed = false): Promise<CWOpportunity[]> {
  let conditions = `contact/id=${contactId}`;
  if (!includeClosed) conditions += ' AND closedDate=null';
  return cwFetch('/sales/opportunities', {
    params: {
      pageSize: '100',
      fields: 'id,name,stage,status,company,contact,closedDate',
      conditions,
      orderBy: 'id desc',
    },
  });
}

export async function createOpportunity(params: {
  name: string;
  contactId: number;
  companyId: number;
  primarySalesRepId?: number;
}): Promise<{ id: number }> {
  // CW requires primarySalesRep on new opportunities. Default to the
  // configured CW member if the caller doesn't pass one explicitly.
  const config = getConfig();
  const repId = params.primarySalesRepId || config.memberId;
  return cwFetch('/sales/opportunities', {
    method: 'POST',
    body: {
      name: params.name,
      company: { id: params.companyId },
      contact: { id: params.contactId },
      primarySalesRep: { id: repId },
    },
  });
}

// ─── Work Types ─────────────────────────────────────────────────

export interface CWWorkType {
  id: number;
  name?: string;
  billTime?: string; // 'Billable', 'DoNotBill', 'NoCharge', 'NoDefault'
}

export async function fetchWorkTypes(): Promise<CWWorkType[]> {
  return cwFetch('/time/workTypes', {
    params: { pageSize: '100', fields: 'id,name,billTime' },
  });
}

// ─── Time Entries ───────────────────────────────────────────────

export async function createTimeEntry(params: {
  memberId: number;
  chargeToType: 'Ticket' | 'Opportunity';
  chargeToId: number;
  timeStart: string;
  timeEnd?: string;
  actualHours: number;
  notes?: string;
  workTypeId?: number;
  billableOption?: 'Billable' | 'DoNotBill' | 'NoCharge' | 'NoDefault';
}): Promise<{ id: number }> {
  const config = getConfig();
  // 'Ticket' charges to the service ticket. 'Opportunity' pre-sales time is booked
  // against the sales Activity we just logged — callers pass that activity's id as
  // chargeToId — so chargeToType is 'Activity'. (The old 'ProjectTicket' branch was
  // dead, and charging 'Activity' against the opportunity id 400s / mis-books.)
  const chargeToType = params.chargeToType === 'Ticket' ? 'ServiceTicket' : 'Activity';
  const body: Record<string, unknown> = {
    member: { id: params.memberId || config.memberId },
    chargeToId: params.chargeToId,
    chargeToType,
    timeStart: params.timeStart,
    actualHours: params.actualHours,
  };
  if (params.timeEnd) body.timeEnd = params.timeEnd;
  if (params.notes) body.notes = params.notes;
  if (params.workTypeId) body.workType = { id: params.workTypeId };
  if (params.billableOption) body.billableOption = params.billableOption;
  return cwFetch('/time/entries', { method: 'POST', body });
}

// ─── Follow-up Activities ───────────────────────────────────────

let followUpTypeIdCache: number | null = null;

async function resolveFollowUpTypeId(): Promise<number> {
  if (followUpTypeIdCache !== null) return followUpTypeIdCache;
  return resolveActivityTypeId(/follow/i, 1);
}

// CW activity type/status ids are tenant-specific — never hardcode them.
// Resolve by name from the tenant (getActivityTypes/getActivityStatuses),
// cache per match pattern, and fall back to the historical default only if
// the tenant has no matching name. Mirrors resolveFollowUpTypeId.
const activityTypeIdCache: Record<string, number> = {};
const activityStatusIdCache: Record<string, number> = {};

async function resolveActivityTypeId(match: RegExp, fallback: number): Promise<number> {
  const key = match.source;
  if (activityTypeIdCache[key] !== undefined) return activityTypeIdCache[key];
  const types = await getActivityTypes();
  const found = types.find(t => match.test(t.name || ''));
  return (activityTypeIdCache[key] = found?.id ?? fallback);
}

async function resolveActivityStatusId(match: RegExp, fallback: number): Promise<number> {
  const key = match.source;
  if (activityStatusIdCache[key] !== undefined) return activityStatusIdCache[key];
  const statuses = await getActivityStatuses();
  const found = statuses.find(s => match.test(s.name || ''));
  return (activityStatusIdCache[key] = found?.id ?? fallback);
}

export async function createFollowUpActivity(params: {
  contactId: number;
  companyId: number;
  notes: string;
  dateStart: string;
  ticketId?: number;
  opportunityId?: number;
}): Promise<{ id: number }> {
  const config = getConfig();
  const typeId = await resolveFollowUpTypeId();
  const openStatusId = await resolveActivityStatusId(/open/i, 1);
  const body: Record<string, unknown> = {
    name: `Follow-up: ${params.notes.slice(0, 60)}`,
    type: { id: typeId },
    status: { id: openStatusId },
    assignTo: { id: config.memberId },
    company: { id: params.companyId },
    dateStart: params.dateStart,
    notes: params.notes,
  };
  // Omit contact entirely when there is none — CW 400s on contact:{id:0}.
  if (params.contactId) body.contact = { id: params.contactId };
  if (params.ticketId) body.ticket = { id: params.ticketId };
  if (params.opportunityId) body.opportunity = { id: params.opportunityId };
  return cwFetch('/sales/activities', { method: 'POST', body });
}

// ─── Activities ─────────────────────────────────────────────────

export async function getActivityTypes(): Promise<CWActivityType[]> {
  return cwFetch('/sales/activities/types', { params: { pageSize: '100' } });
}

export async function getActivityStatuses(): Promise<CWActivityType[]> {
  return cwFetch('/sales/activities/statuses', { params: { pageSize: '100' } });
}

export async function createEmailActivity(params: {
  subject: string;
  contactId: number;
  companyId: number;
  email: string;
  notes: string;
  dateStart: string;
  ticketId?: number;
  opportunityId?: number;
  statusId?: number;
}): Promise<{ id: number }> {
  const config = getConfig();

  // Resolve tenant-specific ids by name (Email type, Closed status — the email
  // already happened), falling back to the historical defaults only if absent.
  const emailTypeId = await resolveActivityTypeId(/e-?mail/i, 7);
  const closedStatusId = params.statusId ?? await resolveActivityStatusId(/closed|complete/i, 2);

  const body: Record<string, unknown> = {
    name: `Email: ${params.subject.slice(0, 80)}`,
    type: { id: emailTypeId },
    status: { id: closedStatusId },
    assignTo: { id: config.memberId },
    company: { id: params.companyId },
    email: params.email,
    dateStart: params.dateStart.replace(/\.\d{3}Z$/, 'Z'),
    notes: params.notes,
  };

  // Omit contact entirely for company-only logging — CW 400s on contact:{id:0}.
  if (params.contactId) body.contact = { id: params.contactId };
  if (params.ticketId) body.ticket = { id: params.ticketId };
  if (params.opportunityId) body.opportunity = { id: params.opportunityId };

  return cwFetch('/sales/activities', { method: 'POST', body });
}

// ─── Meeting Activities ─────────────────────────────────────────

export async function createMeetingActivity(params: {
  subject: string;
  contactId: number;
  companyId: number;
  notes: string;
  dateStart: string;
  dateEnd: string;
  location?: string;
  ticketId?: number;
  opportunityId?: number;
}): Promise<{ id: number }> {
  const config = getConfig();

  // CW `where` field expects a ServiceLocationReference (id-based), not a
  // free-text string. Calendar "location" is often "Microsoft Teams Meeting"
  // or a raw address — prepend it to notes so nothing is lost.
  const notes = params.location
    ? `Location: ${params.location}\n\n${params.notes}`
    : params.notes;

  // Resolve tenant-specific ids by name. Past meetings are logged after-the-fact
  // as Closed; future meetings (a scheduled follow-up logged ahead of time) stay
  // Open so they show in the user's activity queue.
  const appointmentTypeId = await resolveActivityTypeId(/appoint|meeting/i, 2);
  const endMs = new Date(params.dateEnd).getTime();
  const isPast = Number.isFinite(endMs) && endMs < Date.now();
  const statusId = isPast
    ? await resolveActivityStatusId(/closed|complete/i, 2)
    : await resolveActivityStatusId(/open/i, 1);

  const body: Record<string, unknown> = {
    name: `Meeting: ${params.subject.slice(0, 80)}`,
    type: { id: appointmentTypeId },
    status: { id: statusId },
    assignTo: { id: config.memberId },
    company: { id: params.companyId },
    dateStart: params.dateStart.replace(/\.\d{3}Z$/, 'Z'),
    dateEnd: params.dateEnd.replace(/\.\d{3}Z$/, 'Z'),
    notes,
  };

  // Omit contact entirely for company-only logging — CW 400s on contact:{id:0}.
  if (params.contactId) body.contact = { id: params.contactId };
  if (params.ticketId) body.ticket = { id: params.ticketId };
  if (params.opportunityId) body.opportunity = { id: params.opportunityId };

  return cwFetch('/sales/activities', { method: 'POST', body });
}

// ─── Members ────────────────────────────────────────────────────

export async function fetchMembers(): Promise<CWMember[]> {
  return cwFetch('/system/members', {
    params: {
      pageSize: '200',
      fields: 'id,firstName,lastName,identifier',
      conditions: 'inactiveFlag=false',
    },
  });
}

// ─── Test Connection ────────────────────────────────────────────

export async function testConnection(): Promise<boolean> {
  await cwFetch<CWMember[]>('/system/members', { params: { pageSize: '1', fields: 'id' } });
  return true;
}
